import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '@/components/common/Button';
import MainLayout from '@/components/layout/MainLayout';
import ReciboTable from '@/components/recibos/ReciboTable';
import ReciboTypeTabs from '@/components/recibos/ReciboTypeTabs';
import { useAuth } from '@/contexts/AuthContext';
import reciboService from '@/services/reciboService';
import { TipoPagoRecibo } from '@/types/recibo';
import {
  emptyReciboListResult,
  normalizeReciboListResult,
  parseReciboReportTab,
  reciboReportTabToType,
  type ReciboReportTab,
  type ReciboListResult,
} from '@/utils/reciboListUtils';

const PAGE_SIZE = 25;

export default function ReciboList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const requestedTab = searchParams.get('tab');
  const activeTab = parseReciboReportTab(requestedTab);
  const reportType = reciboReportTabToType(activeTab);
  const [result, setResult] = useState<ReciboListResult>(emptyReciboListResult);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  const loadReceipts = useCallback(async (nextPage: number, query: string, type: TipoPagoRecibo) => {
    setLoading(true);
    try {
      const cleanQuery = query.trim();
      const response = await reciboService.searchRecibos({
        page: nextPage,
        limit: PAGE_SIZE,
        tipo_pago: type,
        estado: 'ACTIVO',
        ...(cleanQuery ? { q: cleanQuery } : {}),
      });
      const nextResult = normalizeReciboListResult(response, nextPage, PAGE_SIZE);
      setResult(nextResult);
      setPage(nextResult.page);
    } catch (error) {
      console.error('Error fetching receipts:', error);
      setResult(emptyReciboListResult);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (requestedTab !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
      return;
    }
    setPage(1);
    void loadReceipts(1, activeSearch, reportType);
  }, [activeSearch, activeTab, loadReceipts, reportType, requestedTab, setSearchParams]);

  const changeTab = (tab: ReciboReportTab) => {
    if (tab === activeTab) return;
    setSearchParams({ tab });
  };

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextSearch = searchDraft.trim();
    if (nextSearch === activeSearch) {
      void loadReceipts(1, nextSearch, reportType);
      return;
    }
    setActiveSearch(nextSearch);
  };

  const clearSearch = () => {
    setSearchDraft('');
    if (activeSearch === '') {
      void loadReceipts(1, '', reportType);
      return;
    }
    setActiveSearch('');
  };

  return (
    <MainLayout>
      <div className="mx-auto max-w-[1680px] p-4 sm:p-6">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-950">Recibos</h1>
            <p className="mt-1 text-gray-600">Gestión y consulta completa de recibos de pago</p>
          </div>
          {hasPermission('receipts', 'write') ? (
            <Button onClick={() => navigate('/receipts/new')}>+ Crear Nuevo Recibo</Button>
          ) : null}
        </div>

        <ReciboTypeTabs activeTab={activeTab} onChange={changeTab} />

        <form onSubmit={submitSearch} className="mb-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex-1">
              <label htmlFor="receipt-search" className="sr-only">Buscar recibos</label>
              <input
                id="receipt-search"
                type="search"
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                placeholder="Buscar por recibo, contrato, inquilino o propietario"
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={loading}>Buscar</Button>
              <Button
                type="button"
                variant="outline"
                onClick={clearSearch}
                disabled={loading || (!searchDraft && !activeSearch)}
              >
                Limpiar
              </Button>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-600">También puede buscar por ID, monto o método de pago.</p>
        </form>

        <div
          id="receipt-report-panel"
          role="tabpanel"
          aria-labelledby={`receipt-tab-${activeTab}`}
        >
          <ReciboTable
            receipts={result.items}
            loading={loading}
            reportType={reportType}
            onReceiptUpdated={() => void loadReceipts(page, activeSearch, reportType)}
            emptyState={
              <div className="py-12 text-center">
                <div className="mb-4 text-5xl">🧾</div>
                <h3 className="mb-2 text-lg font-medium text-gray-900">
                  {activeSearch ? 'No se encontraron recibos' : `No hay recibos de ${activeTab} registrados`}
                </h3>
                <p className="text-gray-600">
                  {activeSearch ? 'Ajuste la búsqueda e intente nuevamente' : 'Los recibos se generan al registrar pagos'}
                </p>
              </div>
            }
          />
        </div>

        {result.items.length > 0 ? (
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-gray-600">
              Mostrando {((page - 1) * PAGE_SIZE) + 1} a {Math.min(page * PAGE_SIZE, result.total)} de {result.total} recibos
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded-lg border border-gray-300 px-4 py-2 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={page <= 1}
                onClick={() => void loadReceipts(page - 1, activeSearch, reportType)}
              >
                Anterior
              </button>
              <span className="px-2 py-2 text-gray-700">Página {page} de {result.totalPages}</span>
              <button
                type="button"
                className="rounded-lg border border-gray-300 px-4 py-2 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={page >= result.totalPages}
                onClick={() => void loadReceipts(page + 1, activeSearch, reportType)}
              >
                Siguiente
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </MainLayout>
  );
}
